import { httpApi } from '@api/http.api.ts';
import type { CommonTableRequest } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CommonVesselGroupResponseType,
    CommonVesselGroupType,
    UserType,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/___shared___/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type DataProcessorOfficerRequest = CommonTableRequest;

export interface DataProcessorOfficerUser {
    id: number;
    firstName: string;
    lastName: string;
    rankOrTitleName: string | null;
    userType: UserType;
}

export interface DataProcessorOfficerAccessResponse {
    id: number;
    modifiedById: number;
    modifiedByTenantAlias: string;
    modifiedBy: string;
    userType: UserType;
    vesselGroups: CommonVesselGroupType[];
    users: DataProcessorOfficerUser[];
    version: string | null;
}

export interface UpsertDataProcessorOfficerAccessRequest {
    userType: UserType;
    userIds: number[];
    vesselGroupIds?: number[];
}

export type DataProcessorOfficerAccessSearchResponse = PaginatedResponse<
    DataProcessorOfficerAccessResponse,
    EmptyResultsDescription
>;
export type DataProcessorOfficerUsersSearchResponse = PaginatedResponse<
    DataProcessorOfficerUser,
    EmptyResultsDescription
>;
export type DataProcessorOfficerVesselGroupsSearchResponse = PaginatedResponse<
    CommonVesselGroupResponseType,
    EmptyResultsDescription
>;

const getBaseUrl = (tenantAlias: string) => `admin-center/tenants/${tenantAlias}/rbac/data-processor-officer-access`;

export const getApiDataProcessorOfficerAccess = (
    tenantAlias: string,
    payload: DataProcessorOfficerRequest,
): Promise<DataProcessorOfficerAccessSearchResponse> =>
    httpApi
        .post<DataProcessorOfficerAccessSearchResponse>(`${getBaseUrl(tenantAlias)}/search`, payload)
        .then(({ data }) => data);

export const getApiDataProcessorOfficerUsers = (
    tenantAlias: string,
    payload: DataProcessorOfficerRequest,
): Promise<DataProcessorOfficerUsersSearchResponse> =>
    httpApi
        .post<DataProcessorOfficerUsersSearchResponse>(`${getBaseUrl(tenantAlias)}/users/search`, payload)
        .then(({ data }) => data);

export const getApiDataProcessorOfficerVesselGroups = (
    tenantAlias: string,
    payload: DataProcessorOfficerRequest,
): Promise<DataProcessorOfficerVesselGroupsSearchResponse> =>
    httpApi
        .post<DataProcessorOfficerVesselGroupsSearchResponse>(
            `${getBaseUrl(tenantAlias)}/vessel-groups/search`,
            payload,
        )
        .then(({ data }) => data);

export const createApiDataProcessorOfficerAccess = (
    tenantAlias: string,
    payload: UpsertDataProcessorOfficerAccessRequest,
): Promise<DataProcessorOfficerAccessResponse> =>
    httpApi.post<DataProcessorOfficerAccessResponse>(getBaseUrl(tenantAlias), payload).then(({ data }) => data);

export const updateApiDataProcessorOfficerAccess = (
    tenantAlias: string,
    accessId: number,
    payload: UpsertDataProcessorOfficerAccessRequest,
): Promise<DataProcessorOfficerAccessResponse> =>
    httpApi
        .put<DataProcessorOfficerAccessResponse>(`${getBaseUrl(tenantAlias)}/${accessId}`, payload)
        .then(({ data }) => data);

export const deleteApiDataProcessorOfficerAccess = (tenantAlias: string, accessId: number): Promise<void> =>
    httpApi.delete<void>(`${getBaseUrl(tenantAlias)}/${accessId}`).then(() => undefined);
